import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { FileText, ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { createNewItem } from '../components/pr-form/prItemTemplate';
import usePRMasterData from '../components/pr-form/usePRMasterData';
import { applyItemChange } from '../components/pr-form/applyItemChange';
import { validatePRForm, cleanPRFormData } from '../components/pr-form/prFormUtils';
import PRFormBody from '../components/pr-form/PRFormBody';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Complete SAP-Standard Purchase Requisition Creation Form.
 * Shared form logic/UI lives in components/pr-form/.
 */
const PRCreateComplete = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [materialPlants, setMaterialPlants] = useState({});

  const { masterData, loadingMasterData, orgDefaults } = usePRMasterData();

  const [formData, setFormData] = useState({
    document_type: 'NB',
    source_determination: false,
    created_by: 'ADMIN',
    items: [createNewItem(10)],
  });

  // Seed header org defaults once master data finishes loading
  useEffect(() => {
    if (orgDefaults.purchasing_organization || orgDefaults.company_code) {
      setFormData((prev) => ({
        ...prev,
        ...(orgDefaults.purchasing_organization ? { purchasing_organization: orgDefaults.purchasing_organization } : {}),
        ...(orgDefaults.company_code ? { company_code: orgDefaults.company_code } : {}),
      }));
    }
  }, [orgDefaults]);

  const handleHeaderChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = await applyItemChange(formData.items, index, field, value, setMaterialPlants, { autoPopulateValuation: true });
    setFormData((prev) => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    const lastItem = formData.items[formData.items.length - 1];
    const newItemNumber = lastItem ? lastItem.item_number + 10 : 10;
    const newItem = createNewItem(newItemNumber);
    newItem.purchasing_organization = formData.purchasing_organization;
    newItem.currency = formData.currency;
    newItem.price_currency = formData.currency;
    setFormData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
    setActiveItemIndex(formData.items.length);
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error('Cannot remove the last item');
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, items: newItems }));
    if (activeItemIndex >= newItems.length) setActiveItemIndex(newItems.length - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePRForm(formData)) return;

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/purchase-requisitions`, cleanPRFormData(formData));
      toast.success(`Purchase Requisition ${response.data.pr_number} created successfully!`);
      navigate('/p2p/pr-list');
    } catch (error) {
      console.error('Error creating PR:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMsg = 'Error creating Purchase Requisition';
      if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMsg = errorDetail.map((err) => (typeof err === 'object' && err.msg ? `${err.loc ? err.loc.join('.') : 'field'}: ${err.msg}` : String(err))).join(', ');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        errorMsg = JSON.stringify(errorDetail);
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingMasterData) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-3 text-lg">Loading master data...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/p2p/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Create Purchase Requisition
              </h1>
              <p className="text-sm text-gray-500">Complete SAP-Standard PR with all 50+ fields</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <PRFormBody
            variant="create"
            formData={formData}
            masterData={masterData}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            activeItemIndex={activeItemIndex}
            setActiveItemIndex={setActiveItemIndex}
            materialPlants={materialPlants}
            handleHeaderChange={handleHeaderChange}
            handleItemChange={handleItemChange}
            addItem={addItem}
            removeItem={removeItem}
          />

          <div className="flex gap-3 justify-end mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/p2p/dashboard')} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700" data-testid="pr-submit-btn">
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" />Create Purchase Requisition</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default PRCreateComplete;
